import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import GoogleAuthButton from './GoogleAuthButton';
import {
  loginUserAction,
  setAuthTokenAction,
  setRefreshTokenAction,
  setUserDataAction,
  setVerificationStatusAction,
} from '../reduxstore/actions/authActions';
import createApiClient from '@/api/axiosInstance';

const SignInPanel = ({ signUpEmail }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const apiClient = createApiClient(navigate, dispatch);

  const [email, setEmail] = useState(signUpEmail);
  const [pswrd, setPswrd] = useState('');
  const [message, setMessage] = useState(['', '', '']);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const [incorrect, setIncorrect] = useState([false, false]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    // trim leading and trailing whitespaces
    const trimmedValue = value.trim();

    if (name === 'email') {
      setEmail(trimmedValue);
    } else if (name === 'password') {
      setPswrd(trimmedValue);
    }
  };

  const validateEmail = () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (email && !emailRegex.test(email)) {
      updateMessage(0, 'Please enter a valid Email ID.');
      updateIncorrect(0, true);
    } else {
      updateMessage(0, '');
      updateIncorrect(0, false);
    }
  };

  const updateMessage = (index, msg) => {
    setMessage((prevMessages) => {
      let updatedMessages = [...prevMessages];
      updatedMessages[index] = msg;
      return updatedMessages;
    });
  };

  const updateIncorrect = (index, incorrect) => {
    setIncorrect((prev) => {
      let updated = [...prev];
      updated[index] = incorrect;
      return updated;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate form inputs
    if (!email || !pswrd) {
      updateMessage(2, 'Please enter all credentials');
      updateIncorrect(0, !email);
      updateIncorrect(1, !pswrd);
      return;
    }

    // Reset validation errors
    updateIncorrect(0, false);
    updateIncorrect(1, false);
    validateEmail();

    // Proceed only if validation passes
    if (message.every((msg) => msg === '')) {
      setIsLoading(true);

      try {
        // Step 1: Fetch user data
        const response = await apiClient.post(
          '/get/user',
          JSON.stringify({
            email,
            password: pswrd,
          }),
          {
            authRequired: false,
          },
        );

        const data = response?.data;
        console.log('User API Response:', data);

        try {
          // Step 2: Fetch user details using x_auth_key
          const userDetailsResponse = await apiClient.get('/user-details/get', {
            authRequired: false,
            headers: {
              'Content-Type': 'application/json',
              'x-auth-token': data.data.x_auth_key,
            },
          });

          if (
            !(
              userDetailsResponse.status >= 200 &&
              userDetailsResponse.status < 300
            )
          ) {
            const errorDetails = userDetailsResponse.data;
            console.error('User Details Fetch Error:', errorDetails);
            throw new Error('Failed to fetch user details');
          }

          const userDetails = userDetailsResponse.data;
          console.log('User Details API Response:', userDetails);

          // Step 3: Map user_type to lowercase role
          const userType = userDetails.data.user_type?.toLowerCase();
          const user_role =
            userType === 'buyer'
              ? 'buyer'
              : userType === 'manufacturer'
                ? 'supplier'
                : null;

          if (!user_role) {
            throw new Error(`Unexpected user type: ${userType}`);
          }

          // Step 4: Store authentication details
          localStorage.setItem(
            'authKeyExpiredAt',
            data.data.auth_key_expired_at,
          );
          localStorage.setItem(
            'refreshTokenExpiredAt',
            data.data.refresh_token_expiration,
          );
          localStorage.setItem('accessToken', data.data.x_auth_key);
          localStorage.setItem('refreshToken', data.data.refresh_token);

          // Step 5: Dispatch Redux actions
          await Promise.all([
            dispatch(setAuthTokenAction(data.data.x_auth_key)),
            dispatch(setRefreshTokenAction(data.data.refresh_token)),
            dispatch(
              loginUserAction({
                x_auth_key: data.data.x_auth_key,
                user_id: userDetails.data.user_id,
                user_type_id: userDetails.data.user_type_id,
                role: user_role,
                company_name: userDetails.data.company_name,
                name: userDetails.data.name,
                email,
              }),
            ),
            dispatch(
              setUserDataAction({
                isVerified: false,
                user_id: userDetails.data.user_id,
                user_type_id: userDetails.data.user_type_id,
                role: user_role,
                token: data.data.x_auth_key,
                company_name: userDetails.data.company_name,
                name: userDetails.data.name,
                email,
              }),
            ),
            dispatch(setVerificationStatusAction(false)),
          ]);

          // Redirect to verification page
          setMessage(['', '', '']);
          setError('');
          navigate('/signin/verification', { replace: true });
        } catch (userDetailsError) {
          console.error(
            'Error Fetching User Details:',
            userDetailsError.message,
          );
          setError('Could not retrieve user details. Please try again.');
        }
      } catch (err) {
        const errorMessage =
          err?.response?.data?.error_message || 'An unexpected error occurred';
        console.error('Login API Error:', errorMessage);

        switch (errorMessage) {
          case 'User does not exists':
            setError('User does not exist. Please sign up.');
            break;
          case 'User is not valid':
            setError('Invalid credentials. Please try again.');
            break;
          case 'Incorrect password':
            setError('Invalid credentials. Please try again.');
            break;
          default:
            setError('An unexpected error occurred. Please try again.');
        }
      } finally {
        setIsLoading(false);
      }
    } else {
      updateMessage(2, '');
    }
  };

  return (
    <div className="flex flex-col space-y-3 items-center justify-center border-gray-400 rounded-lg px-12 py-6 w-5/6">
      <div className="w-full">
        <GoogleAuthButton signin={true} setError={setError} />
      </div>
      <div className="py-2 relative inline-flex items-center justify-center w-full">
        <hr className="w-full h-px border-0 bg-gray-300" />
        <span className="absolute mt-3 mb-2 px-3 font-roboto text-gray-400 text-sm -translate-x-1/2 bg-gray-100 left-1/2">
          or login with email
        </span>
      </div>
      <div className="flex flex-col space-y-3.5 items-start w-full">
        <div className="flex flex-col items-start w-full">
          <span className="font-semibold mb-1 text-gray-500">
            Company Email
          </span>
          <input
            name="email"
            type="email"
            value={email}
            onChange={handleInputChange}
            onBlur={() => validateEmail()}
            className={`rounded-md bg-gray-100 border px-4 py-2 w-full ${
              incorrect[1]
                ? 'outline-red-400  border-red-400 hover:border-red-500'
                : 'outline-veryLightPurple  transition ease-in-out duration-300 border-gray-400/70 hover:border-lightPurple'
            }`}
            placeholder="Enter your email"
          />
        </div>
        <div className="flex flex-col items-start w-full">
          <div className="w-full flex flex-row justify-between items-center">
            <span className="font-semibold mb-1 text-gray-500">Password</span>
            <span
              className="text-sm font-roboto cursor-pointer text-[rgba(118,54,217,1)] font-semibold hover:underline"
              onClick={() => {
                navigate('/forgotPassword', {
                  state: !incorrect[0] ? { email: email } : 'undefined',
                });
              }}
            >
              Forgot Password?
            </span>
          </div>

          <input
            type="password"
            name="password"
            value={pswrd}
            onChange={handleInputChange}
            className={`rounded-lg bg-gray-100 border-[#575757] mb-4 border px-4 py-2 w-full ${
              incorrect[1]
                ? 'outline-red-400  border-red-400 hover:border-red-500'
                : 'outline-veryLightPurple  transition ease-in-out duration-300 border-gray-400/70 hover:border-lightPurple'
            }`}
            placeholder="Enter your password"
          />
        </div>
      </div>
      {message && message.some((msg) => msg !== '') && (
        <div className="mt-3 py-3 px-3 rounded-lg bg-[#F8D7D7] text-[#B64444] text-[10px] w-full">
          {message.map((m, i) => (
            <p className="text-sm" key={i}>
              {m}
            </p>
          ))}
        </div>
      )}
      {error && (
        <div className="mt-3 py-3 px-3 rounded-lg bg-[#F8D7D7] text-[#B64444] text-[10px] w-full">
          <p className="text-sm">{error}</p>
        </div>
      )}
      <button
        onClick={(e) => handleSubmit(e)}
        disabled={isLoading}
        className={`w-full text-white font-roboto font-semibold rounded-lg mt-3 p-2 text-lg transition ease-in-out duration-300
          ${
            isLoading
              ? 'bg-gray-400 cursor-not-allowed'
              : 'bg-darkPurple hover:bg-darkerPurple'
          } flex items-center justify-center`}
      >
        {isLoading ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Logging In...
          </>
        ) : (
          'Log In'
        )}
      </button>
      <p className="text-center text-md mt-2 text-gray-600 font-roboto">
        New to MakerSharks?{' '}
        <button
          onClick={() =>
            navigate('/signup', {
              state: !incorrect[0] ? { email: email } : 'undefined',
            })
          }
          className="cursor-pointer ml-1 text-[rgba(118,54,217,1)] font-semibold hover:underline"
        >
          Sign up!
        </button>
      </p>
    </div>
  );
};

export default SignInPanel;
